import { Button, Card, EmptyState, FormField, Input, SkeletonText } from '@ashniva/ui';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useRiFilesQuery } from '../api-code';
import { shortCommit } from '../freshness';
import { useWorkspace } from '../workspace-context';
import { FileViewer } from './FileViewer';
import { ProblemNotice } from './ProblemNotice';

/** A positive line number from the URL, or null. */
function lineParam(value: string | null): number | null {
  const line = Number(value);
  return Number.isInteger(line) && line > 0 ? line : null;
}

/**
 * The indexed files and the file viewer (P14-05). Which file and lines are open lives in the URL — a path and line
 * numbers only — so a line can be linked to and the back button works.
 */
export function FilesPanel() {
  const { linkId, filesHref } = useWorkspace();
  const [params] = useSearchParams();
  const path = params.get('path');
  const start = lineParam(params.get('start'));
  const end = lineParam(params.get('end')) ?? start;
  const [prefix, setPrefix] = useState('');
  const [draft, setDraft] = useState('');
  const files = useRiFilesQuery(linkId, prefix);

  const onFilter = (event: FormEvent) => {
    event.preventDefault();
    setPrefix(draft.trim().replace(/^\/+|\/+$/g, ''));
  };
  const items = files.data?.pages.flatMap((page) => page.items) ?? [];
  const commit = files.data?.pages[0]?.commit ?? null;

  return (
    <div className="ri-files">
      <Card
        title="Files"
        subtitle={commit ? `Indexed at commit ${shortCommit(commit)}` : undefined}
      >
        <form className="ri-form" onSubmit={onFilter}>
          <FormField label="Directory" hint="For example src/payroll. Empty lists everything.">
            <Input
              value={draft}
              maxLength={400}
              onChange={(event) => setDraft(event.target.value)}
            />
          </FormField>
          <Button type="submit" size="sm">
            Show files
          </Button>
        </form>
        {files.isLoading ? (
          <div aria-busy="true">
            <span className="sr-only">Loading files</span>
            <SkeletonText lines={8} />
          </div>
        ) : null}
        {files.isError ? (
          <ProblemNotice error={files.error} onRetry={() => void files.refetch()} size="sm" />
        ) : null}
        {files.isSuccess && items.length === 0 ? (
          <EmptyState
            size="sm"
            icon="○"
            title="No indexed files here"
            description="Check the directory name, or clear it to list every file."
          />
        ) : null}
        {items.length > 0 ? (
          <nav aria-label="Indexed files">
            <ul className="ri-list">
              {items.map((file) => (
                <li key={file.path}>
                  <Link
                    className="ri-file-link"
                    to={filesHref({ path: file.path })}
                    aria-current={file.path === path ? 'true' : undefined}
                  >
                    {file.path}
                  </Link>
                  {file.isTest ? <span className="ri-muted"> · test</span> : null}
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        {files.hasNextPage ? (
          <Button
            size="sm"
            loading={files.isFetchingNextPage}
            onClick={() => void files.fetchNextPage()}
          >
            More files
          </Button>
        ) : null}
      </Card>
      <Card title={path ?? 'No file open'}>
        {path ? (
          <FileViewer
            linkId={linkId}
            path={path}
            focus={start ? { start, end: Math.max(end ?? start, start) } : null}
            lineHref={(line) => filesHref({ path, start: line, end: line })}
          />
        ) : (
          <EmptyState size="sm" icon="○" title="Choose a file to read it" />
        )}
      </Card>
    </div>
  );
}
